import React, { createContext, useContext, useReducer, useMemo } from 'react';

// ── Constants ──────────────────────────────────────────────
const DEFAULT_SLOT_DURATION = 15;
const DEFAULT_ZOOM = 1.0;
const BASE_PX_PER_MINUTE = 2.67; // ~160px per hour at zoom 1.0 (matches V1)

// ── Action Types ──────────────────────────────────────────
export const ACTIONS = {
  SET_DATE: 'SET_DATE',
  SET_VIEW_MODE: 'SET_VIEW_MODE',
  SET_ZOOM: 'SET_ZOOM',
  SET_SLOT_DURATION: 'SET_SLOT_DURATION',
  SET_STAFF_ORDER: 'SET_STAFF_ORDER',
  SET_STAFF_DATA: 'SET_STAFF_DATA',
  SET_EVENTS: 'SET_EVENTS',
  SET_LOADING: 'SET_LOADING',
  SET_INITIAL_LOAD_COMPLETE: 'SET_INITIAL_LOAD_COMPLETE',
  SET_TIME_BOUNDS: 'SET_TIME_BOUNDS',
  SET_SELECTED_STAFF: 'SET_SELECTED_STAFF',
  SET_FILTER: 'SET_FILTER',
  MOVE_EVENT: 'MOVE_EVENT',
  RESIZE_EVENT: 'RESIZE_EVENT',
  SET_WEEK_DATA: 'SET_WEEK_DATA',
  WEEK_MOVE_EVENT: 'WEEK_MOVE_EVENT',
  WEEK_RESIZE_EVENT: 'WEEK_RESIZE_EVENT',
  SET_WEEK_STAFF_INFO: 'SET_WEEK_STAFF_INFO',
  PARK_EVENT: 'PARK_EVENT',
  UNPARK_EVENT: 'UNPARK_EVENT',
  SET_OTHER_SITE_BLOCKS: 'SET_OTHER_SITE_BLOCKS', // multi-location: bookings at other locations
};

// ── Initial State ─────────────────────────────────────────
export const initialState = {
  // View
  viewMode: 'day',       // 'day' | 'week' | 'month'
  selectedDate: '',      // 'YYYY-MM-DD'
  selectedStaffId: null, // week + month view only
  zoomLevel: DEFAULT_ZOOM,
  slotDurationMinutes: DEFAULT_SLOT_DURATION,

  // Time bounds (derived from staff rota)
  viewStartMinutes: 0,
  viewEndMinutes: 1440,

  // Data
  staffOrder: [],
  staffById: {},
  eventsById: {},
  eventsByStaff: {}, // staffId → DiaryEvent[]

  // Filter: 'all' | 'working' | 'custom'
  filterMode: 'all',
  filteredStaffIds: [], // only used when filterMode === 'custom'

  // Loading
  loadingStaffIds: new Set(),
  initialLoadComplete: false,

  // Week view data: { 'YYYY-MM-DD': DiaryEvent[] }
  weekData: {},
  // Week view per-day staff info: { 'YYYY-MM-DD': { isWorking, startMinutes, endMinutes } }
  weekStaffInfo: {},

  // Shelf: parked appointments (max 3)
  parkedEvents: [],

  // Multi-location: staffId → [{ date, startMinutes, endMinutes, salonName }] at OTHER locations
  otherSiteBlocks: {},
};

// ── Reducer ───────────────────────────────────────────────
function diaryReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_DATE:
      return { ...state, selectedDate: action.payload };

    case ACTIONS.SET_OTHER_SITE_BLOCKS:
      return { ...state, otherSiteBlocks: action.payload || {} };

    case ACTIONS.SET_VIEW_MODE:
      return { ...state, viewMode: action.payload };

    case ACTIONS.SET_ZOOM:
      return {
        ...state,
        zoomLevel: Math.max(0.5, Math.min(3.0, action.payload)),
      };

    case ACTIONS.SET_SLOT_DURATION:
      return { ...state, slotDurationMinutes: action.payload };

    case ACTIONS.SET_STAFF_ORDER:
      return { ...state, staffOrder: action.payload };

    case ACTIONS.SET_STAFF_DATA: {
      const { staffId, staff, events } = action.payload;
      return {
        ...state,
        staffById: { ...state.staffById, [staffId]: staff },
        eventsByStaff: { ...state.eventsByStaff, [staffId]: events },
        loadingStaffIds: (() => {
          const next = new Set(state.loadingStaffIds);
          next.delete(staffId);
          return next;
        })(),
      };
    }

    case ACTIONS.SET_EVENTS: {
      const { staffId, events } = action.payload;
      return {
        ...state,
        eventsByStaff: { ...state.eventsByStaff, [staffId]: events },
      };
    }

    case ACTIONS.SET_LOADING: {
      const { staffId, loading } = action.payload;
      const next = new Set(state.loadingStaffIds);
      if (loading) next.add(staffId);
      else next.delete(staffId);
      return { ...state, loadingStaffIds: next };
    }

    case ACTIONS.SET_INITIAL_LOAD_COMPLETE:
      return { ...state, initialLoadComplete: true };

    case ACTIONS.SET_TIME_BOUNDS:
      return {
        ...state,
        viewStartMinutes: action.payload.start,
        viewEndMinutes: action.payload.end,
      };

    case ACTIONS.SET_SELECTED_STAFF:
      return { ...state, selectedStaffId: action.payload };

    case ACTIONS.SET_FILTER:
      return {
        ...state,
        filterMode: action.payload.mode,
        filteredStaffIds: action.payload.staffIds || [],
      };

    case ACTIONS.RESIZE_EVENT: {
      const { eventId, staffId, newStartMinutes, newEndMinutes } = action.payload;
      const events = state.eventsByStaff[staffId] || [];
      const resizedEvt = events.find(e => e.id === eventId);

      // For chained segments, clamp resize to not overlap adjacent segments
      let clampedStart = newStartMinutes;
      let clampedEnd = newEndMinutes;
      if (resizedEvt?.isServiceSegment && resizedEvt.chainId) {
        const siblings = events
          .filter(e => e.chainId === resizedEvt.chainId && e.id !== eventId)
          .sort((a, b) => a.startMinutes - b.startMinutes);
        const prevSibling = siblings.filter(s => s.endMinutes <= resizedEvt.startMinutes).pop();
        const nextSibling = siblings.find(s => s.startMinutes >= resizedEvt.endMinutes);
        if (prevSibling && clampedStart < prevSibling.endMinutes) clampedStart = prevSibling.endMinutes;
        if (nextSibling && clampedEnd > nextSibling.startMinutes) clampedEnd = nextSibling.startMinutes;
      }

      return {
        ...state,
        eventsByStaff: {
          ...state.eventsByStaff,
          [staffId]: events.map(e => e.id === eventId ? { ...e, startMinutes: clampedStart, endMinutes: clampedEnd } : e),
        },
      };
    }

    case ACTIONS.MOVE_EVENT: {
      const { eventId, fromStaffId, toStaffId, newStartMinutes } = action.payload;
      const fromEvents = state.eventsByStaff[fromStaffId] || [];
      const evt = fromEvents.find(e => e.id === eventId);
      if (!evt) return state;

      const timeDelta = newStartMinutes - evt.startMinutes;
      const chainId = evt.chainId;
      const crossColumn = fromStaffId !== toStaffId;
      const isChain = chainId && crossColumn && fromEvents.some(e => e.id !== eventId && e.chainId === chainId);

      let movedEvents;
      let remainingFromEvents;
      if (isChain) {
        // Cross-column: move entire chain together preserving gaps
        remainingFromEvents = fromEvents.filter(e => e.chainId !== chainId);
        movedEvents = fromEvents
          .filter(e => e.chainId === chainId)
          .map(e => ({
            ...e,
            staffId: toStaffId,
            startMinutes: e.startMinutes + timeDelta,
            endMinutes: e.endMinutes + timeDelta,
          }));
      } else {
        // Same column or non-chain: move only the dragged segment
        const duration = evt.endMinutes - evt.startMinutes;
        let clampedStart = newStartMinutes;

        // For chained segments in the same column, clamp to not overlap siblings
        if (evt.isServiceSegment && chainId && !crossColumn) {
          const siblings = fromEvents
            .filter(e => e.chainId === chainId && e.id !== eventId)
            .sort((a, b) => a.startMinutes - b.startMinutes);
          const prevSibling = siblings.filter(s => s.endMinutes <= evt.startMinutes).pop();
          const nextSibling = siblings.find(s => s.startMinutes >= evt.endMinutes);
          if (prevSibling && clampedStart < prevSibling.endMinutes) clampedStart = prevSibling.endMinutes;
          if (nextSibling && clampedStart + duration > nextSibling.startMinutes) clampedStart = nextSibling.startMinutes - duration;
        }

        remainingFromEvents = fromEvents.filter(e => e.id !== eventId);
        movedEvents = [{ ...evt, staffId: toStaffId, startMinutes: clampedStart, endMinutes: clampedStart + duration }];
      }

      const toEvents = fromStaffId === toStaffId
        ? remainingFromEvents.concat(movedEvents)
        : (state.eventsByStaff[toStaffId] || []).concat(movedEvents);

      return {
        ...state,
        eventsByStaff: {
          ...state.eventsByStaff,
          [fromStaffId]: fromStaffId === toStaffId ? toEvents : remainingFromEvents,
          [toStaffId]: toEvents,
        },
      };
    }

    case ACTIONS.SET_WEEK_DATA:
      return { ...state, weekData: action.payload };

    case ACTIONS.SET_WEEK_STAFF_INFO:
      return { ...state, weekStaffInfo: action.payload };

    case ACTIONS.WEEK_MOVE_EVENT: {
      const { eventId, fromDateKey, toDateKey, newStartMinutes } = action.payload;
      const fromEvents = state.weekData[fromDateKey] || [];
      const evt = fromEvents.find(e => e.id === eventId);
      if (!evt) return state;

      const timeDelta = newStartMinutes - evt.startMinutes;
      const chainId = evt.chainId;
      const crossDay = fromDateKey !== toDateKey;
      const isChain = chainId && crossDay && fromEvents.some(e => e.id !== eventId && e.chainId === chainId);

      let movedEvents;
      let remainingFromEvents;
      if (isChain) {
        remainingFromEvents = fromEvents.filter(e => e.chainId !== chainId);
        movedEvents = fromEvents
          .filter(e => e.chainId === chainId)
          .map(e => ({
            ...e,
            startMinutes: e.startMinutes + timeDelta,
            endMinutes: e.endMinutes + timeDelta,
          }));
      } else {
        const duration = evt.endMinutes - evt.startMinutes;
        remainingFromEvents = fromEvents.filter(e => e.id !== eventId);
        movedEvents = [{ ...evt, startMinutes: newStartMinutes, endMinutes: newStartMinutes + duration }];
      }

      const toEvents = fromDateKey === toDateKey
        ? remainingFromEvents.concat(movedEvents)
        : (state.weekData[toDateKey] || []).concat(movedEvents);

      return {
        ...state,
        weekData: {
          ...state.weekData,
          [fromDateKey]: fromDateKey === toDateKey ? toEvents : remainingFromEvents,
          [toDateKey]: toEvents,
        },
      };
    }

    case ACTIONS.WEEK_RESIZE_EVENT: {
      const { eventId, dateKey, newStartMinutes: ns, newEndMinutes: ne } = action.payload;
      const events = state.weekData[dateKey] || [];
      const resizedEvt = events.find(e => e.id === eventId);

      let clampedStart = ns;
      let clampedEnd = ne;
      if (resizedEvt?.isServiceSegment && resizedEvt.chainId) {
        const siblings = events
          .filter(e => e.chainId === resizedEvt.chainId && e.id !== eventId)
          .sort((a, b) => a.startMinutes - b.startMinutes);
        const prevSibling = siblings.filter(s => s.endMinutes <= resizedEvt.startMinutes).pop();
        const nextSibling = siblings.find(s => s.startMinutes >= resizedEvt.endMinutes);
        if (prevSibling && clampedStart < prevSibling.endMinutes) clampedStart = prevSibling.endMinutes;
        if (nextSibling && clampedEnd > nextSibling.startMinutes) clampedEnd = nextSibling.startMinutes;
      }

      return {
        ...state,
        weekData: {
          ...state.weekData,
          [dateKey]: events.map(e => e.id === eventId ? { ...e, startMinutes: clampedStart, endMinutes: clampedEnd } : e),
        },
      };
    }

    case ACTIONS.PARK_EVENT: {
      const { event, originStaffId, originDate } = action.payload;
      // Max 3 parked
      if (state.parkedEvents.length >= 3) return state;
      // Don't park same event twice
      if (state.parkedEvents.some(p => p.event.id === event.id)) return state;

      // Collect all chain members (multi-service bookings with processing gaps)
      const fromEvents = state.eventsByStaff[originStaffId] || [];
      const chainId = event.chainId;
      const chainEvents = chainId ? fromEvents.filter(e => e.chainId === chainId) : [event];
      const chainIds = new Set(chainEvents.map(e => e.id));

      // Remove chain from staff column
      const newFromEvents = fromEvents.filter(e => !chainIds.has(e.id));
      // Remove from week data if applicable
      const newWeekData = { ...state.weekData };
      if (originDate && newWeekData[originDate]) {
        newWeekData[originDate] = newWeekData[originDate].filter(e => !chainIds.has(e.id));
      }

      // Park the primary event (with all services data) as a single shelf item
      return {
        ...state,
        eventsByStaff: { ...state.eventsByStaff, [originStaffId]: newFromEvents },
        weekData: newWeekData,
        parkedEvents: [...state.parkedEvents, { event, chainEvents, originStaffId, originDate, parkedAt: new Date() }],
      };
    }

    case ACTIONS.UNPARK_EVENT: {
      const { eventId, restore } = action.payload;
      const parked = state.parkedEvents.find(p => p.event.id === eventId);
      if (!parked) return state;
      const newParked = state.parkedEvents.filter(p => p.event.id !== eventId);
      if (!restore) {
        return { ...state, parkedEvents: newParked };
      }
      // Restore all chain members to original position
      const eventsToRestore = parked.chainEvents || [parked.event];
      const staffEvents = state.eventsByStaff[parked.originStaffId] || [];
      const newWeekData = { ...state.weekData };
      if (parked.originDate && newWeekData[parked.originDate]) {
        newWeekData[parked.originDate] = [...newWeekData[parked.originDate], ...eventsToRestore];
      }
      return {
        ...state,
        parkedEvents: newParked,
        eventsByStaff: { ...state.eventsByStaff, [parked.originStaffId]: [...staffEvents, ...eventsToRestore] },
        weekData: newWeekData,
      };
    }

    default:
      return state;
  }
}

// ── Context ───────────────────────────────────────────────
const DiaryContext = createContext(null);

export function DiaryProvider({ children, initialDate, onEventClick, onEventContextMenu, onSlotClick, skipSlotPopup, onEventDrop, onEventResize, onStaffHeaderAction, onBackToDay, onDayTap, onLoadMonth, onMonthChange }) {
  const [state, dispatch] = useReducer(diaryReducer, {
    ...initialState,
    selectedDate: initialDate || '',
    zoomLevel: (() => {
      const cached = localStorage.getItem('diaryZoomLevel');
      return cached ? parseFloat(cached) : DEFAULT_ZOOM;
    })(),
    slotDurationMinutes: (() => {
      const cached = localStorage.getItem('diarySlotDuration');
      return cached ? parseInt(cached, 10) : DEFAULT_SLOT_DURATION;
    })(),
  });

  const value = useMemo(() => ({ state, dispatch, onEventClick, onEventContextMenu, onSlotClick, skipSlotPopup, onEventDrop, onEventResize, onStaffHeaderAction, onBackToDay, onDayTap, onLoadMonth, onMonthChange }), [state, onEventClick, onEventContextMenu, onSlotClick, skipSlotPopup, onEventDrop, onEventResize, onStaffHeaderAction, onBackToDay, onDayTap, onLoadMonth, onMonthChange]);

  return (
    <DiaryContext.Provider value={value}>
      {children}
    </DiaryContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────
export function useDiary() {
  const ctx = useContext(DiaryContext);
  if (!ctx) throw new Error('useDiary must be used within DiaryProvider');
  return ctx;
}

// ── Helpers ───────────────────────────────────────────────

/** Convert minutes-from-midnight to pixel Y position */
export function pixelY(minutes, viewStartMinutes, zoomLevel) {
  return (minutes - viewStartMinutes) * BASE_PX_PER_MINUTE * zoomLevel;
}

/** Format minutes-from-midnight to "HH:MM" */
export function formatTime(minutes) {
  const hr = Math.floor(minutes / 60);
  const min = minutes % 60;
  return `${hr <= 9 ? '0' + hr : hr}:${min === 0 ? '00' : min <= 9 ? '0' + min : min}`;
}

/** Get the total viewport height in pixels */
export function getViewportHeight(viewStartMinutes, viewEndMinutes, zoomLevel) {
  return pixelY(viewEndMinutes, viewStartMinutes, zoomLevel);
}

export { BASE_PX_PER_MINUTE };
